import React, { useCallback } from 'react';
import { Switch, Typography } from '@material-ui/core';
import mainStyled from 'styles/main';
import {
  ACTIVE,
  PAUSED,
  ARCHIVED_CAMPAIGNS_TYPE,
  ARCHIVED_CAMPAIGNS_WITH_SINGLE_GROUP_TYPE,
  MIX_ARCHIVED_CAMPAIGNS_TYPE,
} from 'consts';
import { useBreakpoints } from 'helpers/styles';
import { useConfirm } from 'react-use-confirm';
import Button from 'components/mui/Button';

export const createRenderSwitchCell = (fn, readonly) => (params) => {
  const { value, id } = params;
  const isEnabled = value === ACTIVE;
  const isDisabled = value === PAUSED;

  if (isEnabled || isDisabled) {
    return (
      <Switch
        onChange={async (event, checked) => {
          fn?.({ ids: [id], status: checked ? ACTIVE : PAUSED });
        }}
        checked={isEnabled}
        disabled={readonly}
      />
    );
  }

  return value;
};

/** Get content for different types of confirm modals */
const getContent = ({
  action,
  selectedRows,
  label,
  productType,
  isGroups,
  selectionModelType,
}) => {
  let text = '';
  let subtext = '';

  switch (action) {
    case 'archive': {
      text =
        selectedRows?.length > 1
          ? 'Are you sure you want to archive these items?'
          : `Are you sure you want to archive ${label}?`;
      subtext = `Archiving allows you to restore it later if needed and you will
        still be able to view it in your archived items.`;
      break;
    }

    case 'restore': {
      if (isGroups) {
        text =
          'You are restoring campaign group(s). The group will be paused by default, and its campaigns will return to their prior state before archiving.';
      } else if (selectionModelType === ARCHIVED_CAMPAIGNS_TYPE) {
        text =
          'You are restoring campaigns. The campaigns will be paused by default.';
      } else if (
        selectionModelType === ARCHIVED_CAMPAIGNS_WITH_SINGLE_GROUP_TYPE
      ) {
        text =
          'This campaign is part of an archived group. Restore the campaign group first to continue.';
      } else if (selectionModelType === MIX_ARCHIVED_CAMPAIGNS_TYPE) {
        text =
          'Campaigns not in archived groups will be restored to pause state. For campaigns in archived groups, you must restore the group first.';
      }
      break;
    }

    case 'pause': {
      text =
        selectedRows?.length > 1
          ? 'Are you sure you want to pause these items?'
          : `Are you sure you want to pause ${label}?`;
      subtext =
        selectedRows?.length > 1
          ? `Pausing these campaign groups will stop the delivery of ${productType} to all its active campaigns.`
          : `Pausing this campaign group will stop the delivery of ${productType} to all its active campaigns.`;
      break;
    }

    default: {
      text = 'You are permanently deleting archived items.';
      subtext =
        'This action cannot be undone and they will be removed from your account forever.';
    }
  }

  return (
    <>
      <mainStyled.Typography
        variant="body1"
        $css="max-width: 772px; margin: 0 auto"
      >
        {text}
      </mainStyled.Typography>
      {subtext && <Typography variant="body1">{subtext}</Typography>}
    </>
  );
};

export const useConfirmActions = ({
  label: labelProp,
  plural = 'these items',
  btnLabel,
  getRowLabel = (row) => row.buyCampaignName,
  rows,
  selectionModel,
  onAction,
  key,
  action = 'delete',
  productType,
  isGroups,
  selectionModelType,
} = {}) => {
  const { openConfirm } = useConfirm();
  const { isSmUp } = useBreakpoints();

  return useCallback(async () => {
    const selectedRows = !selectionModel
      ? rows
      : rows.filter((row) =>
          selectionModel.some((selectedId) => row[key] === selectedId),
        );

    const label =
      selectedRows?.length === 1
        ? labelProp || selectedRows.map(getRowLabel).join(', ')
        : plural;

    return openConfirm((closeConfirm) => ({
      content: getContent({
        action,
        selectedRows,
        label,
        isSmUp,
        productType,
        isGroups,
        selectionModelType,
      }),
      action: (
        <Button
          fullWidth={!isSmUp}
          onClick={() => {
            closeConfirm(true);
            onAction?.(selectedRows);
          }}
          style={{ textTransform: 'capitalize' }}
        >
          {btnLabel || `Yes, ${action}`}
        </Button>
      ),
    }));
  }, [
    btnLabel,
    selectionModelType,
    getRowLabel,
    onAction,
    openConfirm,
    plural,
    rows,
    selectionModel,
    labelProp,
    key,
    action,
    isSmUp,
    productType,
    isGroups,
  ]);
};

const defaultRenderCell = ({ value: valueProp, colDef, row }) => {
  const value = colDef.valueFormatter
    ? colDef.valueFormatter({ value: valueProp, row }) || ''
    : valueProp || '';

  return (
    <div
      style={{
        width: '100%',
        textAlign: colDef.align || 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={String(value)}
    >
      {String(value)}
    </div>
  );
};

export const withDefaultRenderCell = (columns) => {
  return columns.map((col) => {
    if (!col.renderCell) {
      return {
        ...col,
        renderCell: defaultRenderCell,
      };
    }
    return col;
  });
};
